import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const API =
  process.env.REACT_APP_BACKEND_URL
    ? `${process.env.REACT_APP_BACKEND_URL}/api`
    : "/api";
// Scored questions configuration
const SCORED_QUESTIONS = [
  { id: 'q1', text: 'I often feel nervous before or during intimacy.', reversed: false },
  { id: 'q2', text: 'I feel comfortable communicating with a partner about performance concerns.', reversed: true },
  { id: 'q3', text: 'Past experiences with performance problems make me more anxious now.', reversed: false },
  { id: 'q4', text: 'If I feel anxious, I can usually get back into the moment.', reversed: true },
  { id: 'q5', text: 'When intimacy is planned in advance, I feel more pressure to perform.', reversed: false },
  { id: 'q6', text: 'I worry I might not be able to get or stay hard during intimacy.', reversed: false , branched: true },
  { id: 'q7', text: 'I worry I might disappoint my partner during intimacy.', reversed: false },
  { id: 'q8', text: 'Using dating apps makes me feel more pressure to impress sexually or romantically.', reversed: false },
  { id: 'q9', text: 'I worry that my number of matches or likes reflects my attractiveness or worth.', reversed: false },
  { id: 'q10', text: 'Meeting someone from a dating app makes me feel more pressure than meeting someone offline.', reversed: false },
  { id: 'q11', text: 'Seeing people\'s bodies on social media makes me feel less confident about intimacy.', reversed: false },
  { id: 'q12', text: 'I compare my dating or relationship experiences to what I see online.', reversed: false },
  { id: 'q13', text: '', reversed: false, branched: true },
  { id: 'q14', text: '', reversed: false, branched: true },
  { id: 'q15', text: 'I\'ve avoided intimacy because of performance anxiety.', reversed: false },
  { id: 'q16', text: 'I sometimes pretend I\'m fine even when I feel anxious about intimacy.', reversed: false },
  { id: 'q17', text: 'I delay or cancel dates when I\'m worried about sexual performance.', reversed: false },
  { id: 'q18', text: 'I feel confident in my ability to enjoy intimacy regardless of pressure.', reversed: true },
];

const LIKERT_OPTIONS = [
  { value: 1, label: 'Strongly Disagree' },
  { value: 2, label: 'Disagree' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Agree' },
  { value: 5, label: 'Strongly Agree' },
];

function App() {
  const [step, setStep] = useState(0);
  const [consent, setConsent] = useState(false);
  const [demographics, setDemographics] = useState({
    age: '', gender: '', orientation: '', relationship_status: '', region: '', income: ''
  });
  const [scoredAnswers, setScoredAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [contextAnswers, setContextAnswers] = useState({
    age_noticed: '', anxiety_change: '', main_contributor: '', ghosting_pressure: '',
    talk_to: '', open_text: '', diagnosed: '', alcohol_frequency: ''
  });
  const [results, setResults] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState('forward');
  const [displayScore, setDisplayScore] = useState(0);
  const animationFrameRef = useRef(null);

  const getBranchedText = (qId) => {
    // Q6: erection vs. arousal wording
  if (qId === 'q6') {
    if (demographics.gender === 'Male') {
      return 'I worry I might not be able to get or stay hard during intimacy.';
    }
    // Female / non-binary / other
    return 'I worry I might not be able to get or stay aroused during intimacy.';
  }
    if (qId === 'q13') {
      if (demographics.gender === 'Male') return 'Being seen as masculine is important to me.';
      if (demographics.gender === 'Female') return 'Being seen as feminine is important to me.';
      return 'Being seen in terms of gender is important to me.';
    }
    if (qId === 'q14') {
      if (demographics.gender === 'Male') return 'Struggling sexually would make me feel less of a man.';
      if (demographics.gender === 'Female') return 'Struggling sexually would make me feel less of a woman.';
      return 'Struggling sexually would make me less confident in myself.';
    }
    return '';
  };

  const handleNext = () => {
    setIsTransitioning(true);
    setTransitionDirection('forward');
    setTimeout(() => {
      setStep(step + 1);
      setIsTransitioning(false);
      window.scrollTo(0, 0);
    }, 350);
  };

  const handleScoredAnswer = (value) => {
    const currentQ = SCORED_QUESTIONS[currentQuestionIndex];
    setScoredAnswers({...scoredAnswers, [currentQ.id]: value});
    
    setIsTransitioning(true);
    setTransitionDirection('forward');
    setTimeout(() => {
      if (currentQuestionIndex < SCORED_QUESTIONS.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        setStep(step + 1);
      }
      setIsTransitioning(false);
      window.scrollTo(0, 0);
    }, 350);
  };

  const canProceedDemographics = () => {
    return Object.values(demographics).every(v => v !== '');
  };

  const canProceedContext = () => {
    const { age_noticed, anxiety_change, main_contributor, ghosting_pressure, talk_to, diagnosed, alcohol_frequency } = contextAnswers;
    return age_noticed && anxiety_change && main_contributor && ghosting_pressure && talk_to && diagnosed && alcohol_frequency;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const answers = [];
    
    SCORED_QUESTIONS.forEach(q => {
      const value = scoredAnswers[q.id];
      if (value) {
        const text = q.branched ? getBranchedText(q.id) : q.text;
        const branchVariant = q.branched ? 
          (demographics.gender === 'Male' ? 'masculine' : demographics.gender === 'Female' ? 'feminine' : 'neutral') : null;
        answers.push({
          q_id: q.id, prompt_text: text, branch_variant: branchVariant,
          choice_label: LIKERT_OPTIONS.find(opt => opt.value === value)?.label || '',
          numeric_value: value, is_reversed: q.reversed
        });
      }
    });
    
    answers.push(
      { q_id: 'age_noticed', prompt_text: 'At what age did you first notice performance anxiety?', choice_label: contextAnswers.age_noticed, numeric_value: null, is_reversed: false },
      { q_id: 'anxiety_change', prompt_text: 'Compared to 2 years ago, your performance anxiety is:', choice_label: contextAnswers.anxiety_change, numeric_value: null, is_reversed: false },
      { q_id: 'main_contributor', prompt_text: 'What do you think contributes most?', choice_label: contextAnswers.main_contributor, numeric_value: null, is_reversed: false },
      { q_id: 'ghosting_pressure', prompt_text: 'Have you felt more pressure because of fear of being ghosted or replaced?', choice_label: contextAnswers.ghosting_pressure, numeric_value: null, is_reversed: false },
      { q_id: 'talk_to', prompt_text: 'Who would you feel comfortable talking to about performance anxiety?', choice_label: contextAnswers.talk_to, numeric_value: null, is_reversed: false },
      { q_id: 'open_text', prompt_text: 'What\'s one thing that would make it easier for you to feel less pressure around intimacy?', choice_label: contextAnswers.open_text, numeric_value: null, is_reversed: false },
      { q_id: 'diagnosed', prompt_text: 'Diagnosed with a condition that can affect erections?', choice_label: contextAnswers.diagnosed, numeric_value: null, is_reversed: false },
      { q_id: 'alcohol_frequency', prompt_text: 'In the past 3 months, how often did you have 3+ drinks before sex?', choice_label: contextAnswers.alcohol_frequency, numeric_value: null, is_reversed: false }
    );
    
    try {
      const response = await axios.post(`${API}/submit`, { demographics, answers, consent_ok: consent });
      setResults(response.data);
      setIsTransitioning(true);
      setTransitionDirection('forward');
      setTimeout(() => {
        setStep(step + 1);
        setIsTransitioning(false);
      }, 350);
    } catch (error) {
      console.error('Error submitting:', error);
      alert('Error submitting test. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Count-up animation for results score
  useEffect(() => {
    if (step === 6 && results) {
      const targetScore = results.score_pct;
      const duration = 900;
      const startTime = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Ease-out function
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentScore = Math.round(easeOut * targetScore);
        
        setDisplayScore(currentScore);
        
        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate);
        } else {
          // Trigger confetti after count-up finishes
          triggerConfetti();
        }
      };
      
      animate();
      
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }
  }, [step, results]);

  const triggerConfetti = () => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      return; // Skip confetti if user prefers reduced motion
    }

    // Subtle confetti sprinkle - low density, small circles
    const count = 50;
    const defaults = {
      origin: { y: 0.6 },
      zIndex: 1000,
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
        spread: 60,
        startVelocity: 25,
        decay: 0.92,
        scalar: 0.7, // Small circles
        shapes: ['circle'],
        colors: ['#6DECB9', '#7CC6F9', '#F78D3F', '#F7AFCB', '#FFE873'],
        ticks: 100, // 600-800ms duration
      });
    }

    // Single burst
    fire(1, {
      spread: 40,
      startVelocity: 20,
    });
  };

  const handleCopyShareLink = () => {
    const shareUrl = window.location.origin + '/';
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          toast.success('Link copied!', {
            duration: 2000,
            style: {
              background: '#6DECB9',
              color: '#000000',
              border: 'none',
              fontWeight: '700',
            }
          });
        })
        .catch(() => {
          fallbackCopyTextToClipboard(shareUrl);
        });
    } else {
      fallbackCopyTextToClipboard(shareUrl);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      toast.success('Link copied!', {
        duration: 2000,
        style: {
          background: '#6DECB9',
          color: '#000000',
          border: 'none',
          fontWeight: '700',
        }
      });
    } catch (err) {
      toast.error('Failed to copy link', {
        duration: 2000,
      });
    }
    
    document.body.removeChild(textArea);
  };

  const getBandDescription = (band) => {
    const descriptions = {
      'Very Low': "You're pretty relaxed when it comes to intimacy.",
      'Low': "You might feel nerves once in a while, but it doesn't dominate your experience.",
      'Moderate': "You sometimes carry pressure into intimacy, especially in certain contexts.",
      'High': "Intimacy often feels like a performance, and anxiety may affect your experience.",
      'Very High': "You report frequent performance pressure. You may benefit from talking about this with a partner or professional."
    };
    return descriptions[band] || '';
  };

  const getBandColor = (band) => {
    const colors = {
      'Very Low': '#6DECB9',
      'Low': '#7CC6F9',
      'Moderate': '#FFE873',
      'High': '#F78D3F',
      'Very High': '#F7AFCB'
    };
    return colors[band] || '#E0E0E0';
  };

  const currentQuestion = SCORED_QUESTIONS[currentQuestionIndex];
  const questionText = currentQuestion?.branched ? getBranchedText(currentQuestion.id) : currentQuestion?.text;

  return (
    <div className="app-container">
      {/* Welcome */}
      {step === 0 && (
        <div className="page-center">
          <h1 className="main-title">The Performance Pressure Test</h1>
          <p className="subtitle">Discover how dating apps and social media may be shaping intimacy pressures.</p>
          <Button onClick={handleNext} className="btn-primary" data-testid="start-button">Start the Test</Button>
        </div>
      )}

      {/* About */}
      {step === 1 && (
        <div className="page-center">
          <Card className="content-card">
            <h2 className="section-title">What This Test Is About</h2>
            <p className="text-body">This test explores how dating apps, social media, and ideas about masculinity and identity may affect intimacy and sexual performance anxiety.</p>
            <p className="text-body">Some questions are scored to calculate your Performance Pressure Score, while others are included only for research context.</p>
            <p className="text-emphasis">There are no right or wrong answers—just answer honestly.</p>
            <Button onClick={handleNext} className="btn-primary" data-testid="about-next-button">Next</Button>
          </Card>
        </div>
      )}

      {/* Privacy */}
      {step === 2 && (
        <div className="page-center">
          <Card className="content-card">
            <h2 className="section-title">Your Privacy Matters</h2>
            <p className="text-body">Your responses are anonymous. No personal identifiers are collected.</p>
            <p className="text-body">Data is only used in aggregate for research and discussion.</p>
            <p className="text-body">You can skip the test anytime.</p>
            <div className="consent-box">
              <Checkbox id="consent" checked={consent} onCheckedChange={setConsent} data-testid="consent-checkbox" />
              <Label htmlFor="consent" className="consent-label">I consent to participate anonymously.</Label>
            </div>
            <Button onClick={handleNext} className="btn-primary" disabled={!consent} data-testid="privacy-next-button">I Agree, Continue</Button>
          </Card>
        </div>
      )}

      {/* Demographics */}
      {step === 3 && (
        <div className="page-center">
          <Card className="content-card">
            <h2 className="section-title">About You</h2>
            <div className="form-group">
              <Label>Age Range</Label>
              <Select value={demographics.age} onValueChange={(v) => setDemographics({...demographics, age: v})}>
                <SelectTrigger data-testid="age-select"><SelectValue placeholder="Select age range" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="<18">&lt;18</SelectItem>
                  <SelectItem value="18-21">18–21</SelectItem>
                  <SelectItem value="22-25">22–25</SelectItem>
                  <SelectItem value="26-30">26–30</SelectItem>
                  <SelectItem value="31-35">31–35</SelectItem>
                  <SelectItem value="36+">36+</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <Label>Gender Identity</Label>
              <Select value={demographics.gender} onValueChange={(v) => setDemographics({...demographics, gender: v})}>
                <SelectTrigger data-testid="gender-select"><SelectValue placeholder="Select gender identity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Nonbinary/Other">Nonbinary/Other</SelectItem>
                  <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <Label>Sexual Orientation</Label>
              <Select value={demographics.orientation} onValueChange={(v) => setDemographics({...demographics, orientation: v})}>
                <SelectTrigger data-testid="orientation-select"><SelectValue placeholder="Select sexual orientation" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Straight">Straight</SelectItem>
                  <SelectItem value="Gay/Lesbian">Gay/Lesbian</SelectItem>
                  <SelectItem value="Bi/Pan">Bi/Pan</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                  <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <Label>Relationship Status</Label>
              <Select value={demographics.relationship_status} onValueChange={(v) => setDemographics({...demographics, relationship_status: v})}>
                <SelectTrigger data-testid="relationship-select"><SelectValue placeholder="Select relationship status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Single">Single</SelectItem>
                  <SelectItem value="In a relationship">In a relationship</SelectItem>
                  <SelectItem value="Married">Married</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <Label>Region</Label>
              <Select value={demographics.region} onValueChange={(v) => setDemographics({...demographics, region: v})}>
                <SelectTrigger data-testid="region-select"><SelectValue placeholder="Select region" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="North America">North America</SelectItem>
                  <SelectItem value="Europe">Europe</SelectItem>
                  <SelectItem value="Asia">Asia</SelectItem>
                  <SelectItem value="Latin America">Latin America</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <Label>Income Level</Label>
              <Select value={demographics.income} onValueChange={(v) => setDemographics({...demographics, income: v})}>
                <SelectTrigger data-testid="income-select"><SelectValue placeholder="Select income level" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="<$25k">&lt;$25k</SelectItem>
                  <SelectItem value="$25-49k">$25–49k</SelectItem>
                  <SelectItem value="$50-74k">$50–74k</SelectItem>
                  <SelectItem value="$75-99k">$75–99k</SelectItem>
                  <SelectItem value="$100k+">$100k+</SelectItem>
                  <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleNext} className="btn-primary" disabled={!canProceedDemographics()} data-testid="demographics-next-button">Next</Button>
          </Card>
        </div>
      )}

      {/* Scored Questions - One per page */}
      {step === 4 && currentQuestion && (
        <div className="page-center question-page" key={`question-${currentQuestionIndex}`}>
          <div className="progress-indicator">Question {currentQuestionIndex + 1} of 18</div>
          <div className="question-card">
            <h3 className="question-text">{questionText}</h3>
            <div className="likert-buttons">
              {LIKERT_OPTIONS.map(option => (
                <button
                  key={option.value}
                  onClick={() => handleScoredAnswer(option.value)}
                  className="likert-button"
                  data-testid={`${currentQuestion.id}-option-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Context Questions */}
      {step === 5 && (
        <div className="page-center">
          <Card className="content-card">
            <h2 className="section-title">Additional Context</h2>
            <p className="text-body">These questions help us understand your experience better.</p>
            
            <div className="form-group">
              <Label>At what age did you first notice performance anxiety?</Label>
              <Select value={contextAnswers.age_noticed} onValueChange={(v) => setContextAnswers({...contextAnswers, age_noticed: v})}>
                <SelectTrigger data-testid="age-noticed-select"><SelectValue placeholder="Select age" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="<18">&lt;18</SelectItem>
                  <SelectItem value="18-21">18–21</SelectItem>
                  <SelectItem value="22-25">22–25</SelectItem>
                  <SelectItem value="26-30">26–30</SelectItem>
                  <SelectItem value="31+">31+</SelectItem>
                  <SelectItem value="Never">Never</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label>Compared to 2 years ago, your performance anxiety is:</Label>
              <Select value={contextAnswers.anxiety_change} onValueChange={(v) => setContextAnswers({...contextAnswers, anxiety_change: v})}>
                <SelectTrigger data-testid="anxiety-change-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Much lower">Much lower</SelectItem>
                  <SelectItem value="Lower">Lower</SelectItem>
                  <SelectItem value="About the same">About the same</SelectItem>
                  <SelectItem value="Higher">Higher</SelectItem>
                  <SelectItem value="Much higher">Much higher</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label>What do you think contributes most?</Label>
              <Select value={contextAnswers.main_contributor} onValueChange={(v) => setContextAnswers({...contextAnswers, main_contributor: v})}>
                <SelectTrigger data-testid="contributor-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Dating apps">Dating apps</SelectItem>
                  <SelectItem value="Social media">Social media</SelectItem>
                  <SelectItem value="Porn">Porn</SelectItem>
                  <SelectItem value="Partner expectations">Partner expectations</SelectItem>
                  <SelectItem value="Stress">Stress</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label>Have you felt more pressure because of fear of being ghosted or replaced?</Label>
              <Select value={contextAnswers.ghosting_pressure} onValueChange={(v) => setContextAnswers({...contextAnswers, ghosting_pressure: v})}>
                <SelectTrigger data-testid="ghosting-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                  <SelectItem value="Sometimes">Sometimes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label>Who would you feel comfortable talking to about performance anxiety?</Label>
              <Select value={contextAnswers.talk_to} onValueChange={(v) => setContextAnswers({...contextAnswers, talk_to: v})}>
                <SelectTrigger data-testid="talk-to-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Partner">Partner</SelectItem>
                  <SelectItem value="Friends">Friends</SelectItem>
                  <SelectItem value="Doctor/therapist">Doctor/therapist</SelectItem>
                  <SelectItem value="No one">No one</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label>What's one thing that would make it easier for you to feel less pressure around intimacy?</Label>
              <Textarea 
                value={contextAnswers.open_text} 
                onChange={(e) => setContextAnswers({...contextAnswers, open_text: e.target.value})}
                placeholder="Share your thoughts (optional)..."
                data-testid="open-text-area"
              />
            </div>

            <div className="form-group">
              <Label>Diagnosed with a condition that can affect erections?</Label>
              <Select value={contextAnswers.diagnosed} onValueChange={(v) => setContextAnswers({...contextAnswers, diagnosed: v})}>
                <SelectTrigger data-testid="diagnosed-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                  <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label>In the past 3 months, how often did you have 3+ drinks before sex?</Label>
              <Select value={contextAnswers.alcohol_frequency} onValueChange={(v) => setContextAnswers({...contextAnswers, alcohol_frequency: v})}>
                <SelectTrigger data-testid="alcohol-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Never">Never</SelectItem>
                  <SelectItem value="Rarely">Rarely</SelectItem>
                  <SelectItem value="Sometimes">Sometimes</SelectItem>
                  <SelectItem value="Often">Often</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button onClick={handleSubmit} className="btn-primary" disabled={!canProceedContext() || isSubmitting} data-testid="context-submit-button">
              {isSubmitting ? 'Calculating...' : 'See My Results'}
            </Button>
          </Card>
        </div>
      )}

      {/* Results */}
      {step === 6 && results && (
        <div className="page-center results-page">
          <h2 className="results-title">Your Performance Pressure Score</h2>
          <div className="score-display">
            <div className="score-number score-animate" data-testid="score-display">{displayScore}</div>
            <div className="score-label">out of 100</div>
          </div>
          <div className="band-pill band-animate" style={{backgroundColor: getBandColor(results.band)}} data-testid="band-display">
            {results.band}
          </div>
          <p className="band-description description-animate">{getBandDescription(results.band)}</p>
          <div className="interpretation-box interpretation-animate">
            <p className="text-body">Your score reflects how much performance pressure you experience in intimate situations. This test considers factors like dating app usage, social media exposure, and personal anxiety patterns.</p>
            <p className="text-body">Remember: Performance anxiety is common and treatable. If this is affecting your quality of life, consider speaking with a healthcare professional or therapist who specializes in sexual health.</p>
          </div>
          <Button onClick={handleCopyShareLink} className="btn-share share-animate" data-testid="share-button">
            Copy share link
          </Button>
        </div>
      )}
      <Toaster />
    </div>
  );
}

export default App;
